import { createQueryKeys } from "@lukemorales/query-key-factory";
import { get, PaginatedResponse, users } from "@/api/api";
import { OrganizationMembership } from "@/user/user";
import { useSuspenseQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { SortingState } from "@tanstack/react-table";
import { apiClient } from "@/api/client";

export const organizationUsers = createQueryKeys('organizationUsers', {
  list: (organizationId: number, sorting: SortingState = []) => {
    const queryKey = [organizationId, sorting];

    return {
      queryKey,
      queryFn: async () => {
        const params = new URLSearchParams({
          page_size: '100'
        });

        if (sorting.length > 0) {
          const sortField = sorting[0];
          let orderingField = sortField.id;

          // Map frontend field names to backend field names
          if (orderingField === 'name') {
            orderingField = 'user__first_name';
          } else if (orderingField === 'email') {
            orderingField = 'user__email';
          } else if (orderingField === 'role') {
            orderingField = 'role';
          }

          const ordering = sortField.desc ? `-${orderingField}` : orderingField;
          params.append('ordering', ordering);
        }

        return await get<PaginatedResponse<OrganizationMembership>>(`organizations/${organizationId}/members/?${params.toString()}`);
      },
    };
  },
});

export const useOrganizationUsers = (organizationId: number, sorting: SortingState = []) => {
  return useSuspenseQuery(organizationUsers.list(organizationId, sorting));
};

export const useUpdateMembershipRole = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({ membershipId, organizationId, role }: { membershipId: number; organizationId: number; role: string }) => {
      const res = await apiClient.patch(`/organizations/${organizationId}/members/${membershipId}/`, { role });
      return res.data;
    },
    onSuccess: () => {
      // Invalidate all organization users queries to refetch the data
      queryClient.invalidateQueries({ queryKey: organizationUsers._def });
    },
  });
};

export const useDeleteMembership = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({ membershipId, organizationId }: { membershipId: number; organizationId: number }) => {
      await apiClient.delete(`/organizations/${organizationId}/members/${membershipId}/`);
    },
    onSuccess: () => {
      // Invalidate all organization users queries to refetch the data
      queryClient.invalidateQueries({ queryKey: organizationUsers._def });
    },
  });
};

export const useUpdateUser = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({ userId, data }: { userId: number; data: { first_name?: string; last_name?: string } }) => {
      const res = await apiClient.patch(`/users/${userId}/`, data);
      return res.data;
    },
    onSuccess: () => {
      // Invalidate user query to refetch the data
      queryClient.invalidateQueries({ queryKey: users._def });
    },
  });
};

export const useValidatePassword = () => {
  return useMutation({
    mutationFn: async ({ password, email }: { password: string; email?: string }) => {
      const res = await apiClient.post<{ valid: boolean; errors?: string[] }>('/validate-password/', {
        password,
        user: email ? { email } : undefined
      });
      return res.data;
    },
  });
};
